import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { contentService } from '@/services/contentService';
import { siteGroupExperienceAtom } from '@/store/siteGroupExperienceAtom';
import { replaceConfigTokens, interpolateContent, repairUtf8Mojibake, type ConfigTokens } from '@/lib/content';
import { isParagraphCodeMode, wrapCode } from '@/lib/paragraphCodeMode';
import { currentLangAtom } from '@/store/langAtom';
import { annotationModeAtom } from '@/store/annotationModeAtom';
import { PpAnnotationPin } from '@/components/PpAnnotationPin';
// content-defaults.json is the static failsafe bundle — not a language file.
// Keys are paragraph config codes (e.g. "PPMyCode"); values are the DefaultValue
// text from the C# ParagraphConfigSetting class. Imported directly so the fallback
// works without a network call and regardless of which language is active.
import contentDefaults from '@/locales/content-defaults.json';

const defaults = contentDefaults as Record<string, string>;

export function useContent(): {
  /** Wraps text in a span with data-dd-privacy="allow" so paragraph config strings are
   *  visible in Datadog Session Replay under the default mask policy. In annotation mode
   *  also appends a PpAnnotationPin. Use getContentString for aria-label / title / template literals. */
  getContent: (code: string) => ReactNode;
  /** Always returns a plain string — use for aria-label, placeholder, template literals. */
  getContentString: (code: string) => string;
  /**
   * Annotation-aware replacement for `interpolateContent(getContent(code), ...)`.
   * In annotation mode wraps the interpolated result in a span with a PpAnnotationPin.
   * Use for JSX children only — for aria-label / title / setState use getContentString + interpolateContent.
   */
  getInterpolatedContent: (
    code: string,
    posOrNamed: (string | number)[] | Record<string, string | number>,
    named?: Record<string, string | number>,
  ) => ReactNode;
  hasCode: (code: string) => boolean;
  isLoading: boolean;
  /** Apply {Token} replacement to arbitrary text using the active facility's config. */
  replaceTokens: (text: string) => string;
} {
  // currentLangAtom holds the full ISO code (e.g. 'en-AU', 'hr') and updates
  // reactively whenever applyLanguage() is called — no event listener needed.
  const lang           = useAtomValue(currentLangAtom);
  const annotationMode = useAtomValue(annotationModeAtom);
  const experience = useAtomValue(siteGroupExperienceAtom);

  // Track paragraph-code-mode so the hook re-renders when it is toggled.
  const [codeMode, setCodeMode] = useState(isParagraphCodeMode);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'DisplayParagraphConfigMapping') setCodeMode(isParagraphCodeMode());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const { data = [], isLoading } = useQuery({
    queryKey: ['content', lang],
    queryFn: () => contentService.getParagraphs(lang),
    staleTime: 5 * 60 * 1000,   // 5 min
    gcTime: 30 * 60 * 1000,     // 30 min — survives page navigation
  });

  // Memoised Map for O(1) lookups — rebuilt only when the data array reference changes,
  // not on every render.
  const contentMap = useMemo(
    () => new Map(data.map(p => [p.paragraphConfigCode, p.text])),
    [data],
  );

  // Config tokens resolved from the active facility's configOverride — mirrors the
  // Gen2 AppConfig.ResolveConfigStrings() token set so {PracticeName} etc. work.
  const configTokens = useMemo<ConfigTokens>(() => {
    const cfg = experience?.configOverride ?? {};
    return {
      PracticeName:    cfg['PracticeName']    || undefined,
      PortalName:      cfg['PortalName']      || undefined,
      PortalFullName:  cfg['PortalFullName']  || undefined,
      // PortalShortName falls back to PortalName when not separately configured —
      // matches Gen2 behaviour where the short name defaulted to the portal name.
      PortalShortName: cfg['PortalShortName'] || cfg['PortalName'] || undefined,
      CopyrightName:   cfg['CopyrightName']   || undefined,
      SiteName:        cfg['SiteName']        || undefined,
      PracticeURL:     cfg['PracticeURL']     || undefined,
    };
  }, [experience]);

  // Stable across renders — only changes when configTokens changes (i.e. facility switch).
  // useCallback is required so callers can safely include replaceTokens in effect/memo deps.
  const replaceTokens = useCallback(
    (text: string): string => replaceConfigTokens(repairUtf8Mojibake(text), configTokens),
    [configTokens],
  );

  // Fallback chain (in order):
  //   1. Bulk paragraph config Map — API response cached in React Query.
  //      Empty string from the DB is a legitimate value — must NOT fall through.
  //   2. content-defaults.json — static failsafe shipped with the app.
  //      Contains the C# DefaultValue for every registered code.
  //   3. Wrapped code name — «PPCode», same display as DisplayParagraphConfigMapping.
  //      Returns '' while the bulk load is still in-flight to avoid a flash.
  //   Per-code API fallback (step 4) lives in useParagraph, not here.
  //
  // useCallback keeps the reference stable so callers can list getContent as a
  // useEffect / useMemo dependency without triggering infinite re-render loops.
  // Always returns a plain string — safe for aria-label, placeholder, title, template literals.
  const getContentString = useCallback(
    (code: string): string => {
      if (codeMode) return wrapCode(code);
      if (contentMap.has(code)) {
        const raw = contentMap.get(code) ?? '';
        return raw ? replaceTokens(raw) : raw;
      }
      const defaultVal = defaults[code];
      if (defaultVal !== undefined) {
        return defaultVal ? replaceTokens(defaultVal) : defaultVal;
      }
      return isLoading ? '' : wrapCode(code);
    },
    [codeMode, contentMap, replaceTokens, isLoading],
  );

  // Wraps text in data-dd-privacy="allow" so paragraph config strings (non-PHI CMS content)
  // are visible in Datadog Session Replay under the default mask policy. In annotation mode
  // also appends a PpAnnotationPin for admin hover-to-edit.
  const getContent = useCallback(
    (code: string): ReactNode => {
      const text = getContentString(code);
      if (text === '') return text;
      // Strings with interpolation tokens ({{name}} or {0}) may carry PHI — don't mark as allow.
      const safe = !text.includes('{{') && !/\{\d/.test(text);
      if (!annotationMode) return safe ? <span data-dd-privacy="allow">{text}</span> : text;
      return safe
        ? <span data-dd-privacy="allow">{text}<PpAnnotationPin code={code} /></span>
        : <>{text}<PpAnnotationPin code={code} /></>;
    },
    [getContentString, annotationMode],
  );

  // hasCode distinguishes "absent from bulk" (false) from "legitimately empty string" (true).
  // Used by useParagraph to decide whether to fire the per-code fallback request.
  const hasCode = useCallback(
    (code: string): boolean => contentMap.has(code),
    [contentMap],
  );

  const getInterpolatedContent = useCallback(
    (
      code: string,
      posOrNamed: (string | number)[] | Record<string, string | number>,
      named?: Record<string, string | number>,
    ): ReactNode => {
      const text = named !== undefined
        ? interpolateContent(getContentString(code), posOrNamed as (string | number)[], named)
        : interpolateContent(getContentString(code), posOrNamed as Record<string, string | number>);
      if (!annotationMode) return text;
      return <>{text}<PpAnnotationPin code={code} /></>;
    },
    [getContentString, annotationMode],
  );

  return { getContent, getContentString, getInterpolatedContent, hasCode, isLoading, replaceTokens };
}
